// @ts-nocheck
import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import AntSwitch from '@/components/form-control/AntSwitch'
import InputLabelField from '@/components/form-control/InputLabelField'
import Select from '@/components/form-control/Select'
import Refresh from '@/components/icons/Refresh'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import { useSearchControllerId } from '@/features/config/hooks/controller'
import useInputDecoderReadings from '@/features/config/hooks/controller/useInputDecoderReadings'
import useUpdateController from '@/features/config/hooks/controller/useUpdateController'
import useSearchAllIOConfig from '@/features/config/hooks/controller/useSearchAllIOConfig'
import useDecoderProtocolMap from '@/features/config/hooks/controller/useDecoderProtocolMap'
import {
  getIOConfigOptions,
  resolveDecoderProtocol,
} from '@/utils/ioConfigOptions'
import { IoTypeMask } from '@/types/controller'
import useStationLineUnitControl from '@/features/config/hooks/station/useStationLineUnitControl'
import useStationLineUnitDecoder from '@/features/config/hooks/station/useStationLineUnitDecoder'
import { useDateTime } from '@/hooks/useDateTime'
import useTranslation from '@/hooks/useTranslation'
import { useUnits } from '@/hooks/useUnits'
import { formatDecoderValue } from '@/constant/decoder'
import { SearchParams } from '@/types/common'
import { DecoderType } from '@/types/decoder'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Fade,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import * as yup from 'yup'

interface ControllersViewProps {
  id?: string
  address?: string
  twiId?: number
}

const schema = yup
  .object()
  .shape({
    name: yup.string().required('Decoder Description is required'),
    inrushVoltage: yup
      .number()
      .nullable()
      .when('overrideSwitchParams', {
        is: true,
        then: yup
          .number()
          .nullable()
          .required('Inrush Voltage is required')
          .typeError('Inrush Voltage is a number')
          .min(0, 'Minimum 0')
          .test(
            'decimal',
            'Number must be a decimal with one digit after the comma',
            (value) => {
              if (!value) return true
              return /^-?\d+(\.\d)?$/.test(value.toString())
            },
          ),
      }),
    inrushTime: yup
      .number()
      .nullable()
      .when('overrideSwitchParams', {
        is: true,
        then: yup
          .number()
          .nullable()
          .required('Inrush Time is required')
          .typeError('Inrush Time is a number')
          .min(1, 'Minimum 1')
          .max(255, 'Maximum 255')
          .integer('The Inrush Time must be a positive integer'),
      }),
    holdingVoltage: yup
      .number()
      .nullable()
      .when('overrideSwitchParams', {
        is: true,
        then: yup
          .number()
          .nullable()
          .required('Holding Voltage is required')
          .typeError('Holding Voltage is a number')
          .min(0, 'Minimum 0')
          .test(
            'decimal',
            'Number must be a decimal with one digit after the comma',
            (value) => {
              if (!value) return true
              return /^-?\d+(\.\d)?$/.test(value.toString())
            },
          ),
      }),
    // decoderType: yup.string().required('Decoder Type is required'),
    // configuration: yup.string().required('Configuration is required'),
  })
  .required()

const ControllersView = ({
  address,
  id,
  twiId,
}: ControllersViewProps) => {
  const { t } = useTranslation()
  const { formatDateByTimezone } = useDateTime()
  const { convertLabelUnit, convertFromMetric } = useUnits()
  const [decoderType, setDecoderType] = useState<DecoderType[] | undefined>()
  const [dateUpdateControl, setDateControl] = useState<Date | null>(null)
  const [dateUpdateDecode, setDateDecoder] = useState<Date | null>(null)

  const { closeSidebar } = useConfigUI()
  const { interfaceId } = useAuthContext()
  const [searchParams] = useState<SearchParams>({
    PageSize: 10,
  })

  const { data: editItem, isLoading: loadingDecoder } = useSearchControllerId(
    interfaceId,
    id,
  )
  const { mutate: editController, isLoading: loadingController } =
    useUpdateController()

  const { data: ioConfigs } = useSearchAllIOConfig(Number(interfaceId))
  const { data: protocolData } = useDecoderProtocolMap(Number(interfaceId))

  // Line-unit and IO data live on the decoder's own controller. On a control
  // hub that is a sub-controller (the decoder's interfaceId), not the hub, so
  // the readings are fetched against it. Falls back to the current interface
  // for a plain controller.
  const readingsInterfaceId =
    Number(editItem?.interfaceId) || Number(interfaceId)

  const {
    data: dataLineUnitControl,
    isFetching: loadingControl,
    refetch: fetchControl,
  } = useStationLineUnitControl(
    readingsInterfaceId,
    Number(editItem?.address) || 0,
    twiId,
  )

  const {
    data: dataLineUnitDecoder,
    isFetching: loadingDecoderReadings,
    refetch: fetchDecoder,
  } = useStationLineUnitDecoder(
    readingsInterfaceId,
    Number(editItem?.address) || 0,
    Number(editItem?.index) || 0,
    twiId,
  )

  const {
    data: dataInputDecoder,
    isFetching: loadingInputDecoder,
    refetch: fetchInputDecoder,
  } = useInputDecoderReadings(
    readingsInterfaceId,
    Number(editItem?.address) || 0,
    Number(editItem?.index) || 0,
  )

  const isInputDecoder = editItem?.type === 'input'
  const isLoadingReadings = isInputDecoder
    ? loadingInputDecoder
    : loadingDecoderReadings

  const isLoading = loadingController

  const inputRefSiteName = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRefSiteName.current) {
      inputRefSiteName.current.focus()
    }
  }, [])

  const form = useForm({
    defaultValues: {
      id: '',
      name: '',
      address: '',
      index: '',
      decoderType: '',
      verifySum: '',
      type: '',
      configuration: '',
      configState: '',
      attention: '',
      value: '',
      overrideOptions: false,
      verify: false,
      force: false,
      overrideSwitchParams: false,
      inrushVoltage: 0,
      inrushTime: 0,
      holdingVoltage: 0,
      ...editItem,
    },
    resolver: yupResolver(schema),
  })

  const switchOption = useWatch({
    control: form.control,
    name: 'overrideOptions',
  })

  const switchParameters = useWatch({
    control: form.control,
    name: 'overrideSwitchParams',
  })

  useEffect(() => {
    if (editItem?.id) {
      form.reset({
        id: editItem.id,
        name: editItem.name,
        address: editItem.address,
        index: editItem.index,
        decoderType: editItem.decoderType,
        type: editItem.type,
        verifySum: editItem.verifySum,
        configuration: editItem.configuration,
        configState: editItem.configState,
        attention: editItem.attention,
        value:
          editItem.value === 0
            ? t('dashboardControllerStatesOff')
            : t('dashboardControllerStatesOn'),
        overrideOptions: editItem.overrideOptions,
        verify: editItem.verify,
        force: editItem.force,
        overrideSwitchParams: editItem.overrideSwitchParams,
        inrushVoltage: editItem.inrushVoltage,
        inrushTime: editItem.inrushTime,
        holdingVoltage: editItem.holdingVoltage,
        twiId: editItem.twiId,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editItem, form])

  const expandItem = (item: any, maxLevel: number = 3) => {
    maxLevel--
    let result = []
    if (maxLevel > 0) {
      result.push(item)
      if (item.data) result.push(...expandHierarchy(item.data, maxLevel))
    }
    return result
  }
  const expandHierarchy = (array: any[], maxLevel: number = 3): any[] => {
    let result = []
    for (const i of array) {
      result.push(...expandItem(i, maxLevel))
    }
    return result
  }

  const getDecoder = async () => {
    try {
      const { data } = await controllerDataConfigApi
        .getDecoderType({ interfaceId: Number(interfaceId) ?? 0 })
        .then((data) => {
          let result = expandHierarchy(data.data.data, 4)
          // console.log('output', result, ...data.data.data)
          const combined = [...result]
          data.data.data = combined
          setDecoderType(combined)
          // console.log('combine', combined, '\n data', data)
          return data
        })
      return data
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
    }
  }
  // A configuration belongs to one decoder type and one line unit protocol, so
  // offer only the ones this decoder can take. The per-decoder endpoint does
  // the same filtering server-side but returns nothing when the interface has
  // no line unit types, which left this select empty and made saving write 0.
  const configurationOptions = useMemo(() => {
    const ioType =
      editItem?.type === 'output' ? IoTypeMask.Output : IoTypeMask.Input

    // Scope to the decoder's own sub-controller: config ids repeat across the
    // sub-controllers of a control hub.
    const subId = Number(editItem?.interfaceId) || Number(interfaceId)
    const subConfigs =
      ioConfigs?.filter((cfg) => Number(cfg.interfaceId) === subId) ?? []

    return getIOConfigOptions(
      subConfigs,
      ioType,
      resolveDecoderProtocol(
        subConfigs,
        protocolData?.protocolByAddress.get(Number(editItem?.address)),
        Number(editItem?.configurationId),
      ),
    ).map((option) => ({ id: option.id, description: option.label }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ioConfigs,
    protocolData,
    editItem?.type,
    editItem?.address,
    editItem?.configurationId,
    editItem?.interfaceId,
    interfaceId,
  ])

  const getConfiguration = async () => configurationOptions

  useEffect(() => {
    if (decoderType && decoderType.length > 0) {
      const value = decoderType.find(
        (i: any) => i.id === editItem?.decoderTypeId || 0,
      )

      form.setValue('decoderType', value as any)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decoderType, editItem])

  // Preselect the decoder's stored configuration once the options are known.
  useEffect(() => {
    if (!configurationOptions.length) return
    const value = configurationOptions.find(
      (option) => option.id === editItem?.configurationId,
    )
    if (value) form.setValue('configuration', value as any)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurationOptions, editItem])

  const handleSave = (data: any) => {
    editController({
      id: id,
      // On a control hub the decoder belongs to a sub-controller and must be
      // written back to its own interface, not the hub's. Fall back to the
      // current interface for a plain (non-hub) controller.
      interfaceId: Number(editItem?.interfaceId) || Number(interfaceId),
      address: data.address,
      name: data.name,
      decoderType: data.decoderType && Number(data.decoderType.id),
      // Keep the stored configuration when nothing is selected: sending 0
      // reset the decoder to "Unknown".
      configuration:
        data.configuration?.id !== undefined && data.configuration?.id !== null
          ? Number(data.configuration.id)
          : (editItem?.configurationId ?? 0),
      // verify: switchOption ? data.verify : false,
      // force: switchOption ? data.force : false,
      // inrushVoltage: switchParameters ? Number(data.inrushVoltage) : 0,
      // inrushTime: switchParameters ? Number(data.inrushTime) : 0,
      // holdingVoltage: switchParameters ? Number(data.holdingVoltage) : 0,
      // overrideSwitchParams: data.overrideSwitchParams,
      // overrideOptions: data.overrideOptions,
      twiId: data.twiId,
    })
  }

  return (
    <Container sx={{ maxWidth: '450px', height: '100vh' }}>
      {loadingDecoder && (
        <Stack
          width="377px"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      )}
      {!loadingDecoder && (
        <Fade in={!loadingDecoder}>
          <form onSubmit={form.handleSubmit(handleSave)}>
            <Stack sx={{ padding: '34px 18px' }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '377px' }}
              >
                <Typography
                  sx={{
                    fontWeight: '500',
                    lineHeight: '14px',
                    color: 'text.main',
                    fontSize: '14px',
                  }}
                >
                  {t('editDecoder')}
                </Typography>
                <Stack direction="row">
                  <Button
                    variant="secondary"
                    sx={{ marginRight: '7px', width: '85px', height: '26px' }}
                    onClick={() => closeSidebar()}
                  >
                    {t('commonActionsCancel')}
                  </Button>
                  <LoadingButton
                    loading={isLoading}
                    variant="primary"
                    size="small"
                    sx={{ minWidth: '72px', height: '26px' }}
                    type="submit"
                  >
                    {t('commonActionsSave')}
                  </LoadingButton>
                </Stack>
              </Stack>
              <InputLabelField
                isRequired
                name="name"
                form={form}
                label={t('decoderName')}
                placeholder={t('ThisIsAText')}
                inputRef={inputRefSiteName}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  width: { xs: '377px' },
                  marginTop: '22px',
                }}
                padding="8px 16px"
              />
              <InputLabelField
                disabled
                name="address"
                form={form}
                label={t('formsLabelsAddress')}
                placeholder={t('ThisIsAText')}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  width: { xs: '377px' },
                  marginTop: '8px',
                }}
                padding="8px 16px"
              />
              <InputLabelField
                disabled
                name="index"
                form={form}
                label={t('formsLabelsIndex')}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  width: { xs: '377px' },
                  marginTop: '8px',
                }}
                padding="8px 16px"
              />
              <InputLabelField
                disabled
                name="verifySum"
                form={form}
                label={t('VerifySum')}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  width: { xs: '377px' },
                  marginTop: '8px',
                }}
                padding="8px 16px"
              />
              {/* 
          {data && data?.list.length > 0 && (
            <SelectLabelDecoderType
              required
              name="decoderType"
              form={form}
              nameLabel="Decoder Type"
              onChange={handleChangeDecoder}
              value={decoderType}
              sx={{ maxWidth: '377px', marginTop: '8px' }}
            />
          )} */}
              {getDecoder && editItem?.type === 'output' && (
                <Select
                  label="Purpose"
                  name="decoderType"
                  form={form}
                  bindKey="id"
                  bindLabel="name"
                  getOptions={getDecoder}
                  loadOptionInit={true}
                  sx={{
                    width: { xs: '377px' },
                    '& .MuiSelect-select': {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              )}
              <InputLabelField
                disabled
                name="type"
                form={form}
                label={t('commonLabelsType')}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  width: { xs: '377px' },
                  marginTop: '8px',
                }}
                padding="8px 16px"
              />

              {editItem && getConfiguration && (
                <Select
                  label={t('commonLabelsConfiguration')}
                  name="configuration"
                  form={form}
                  bindKey="id"
                  bindLabel="description"
                  getOptions={getConfiguration}
                  loadOptionInit={true}
                  sx={{
                    width: { xs: '377px' },
                    '& .MuiSelect-select': {
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              )}
              <InputLabelField
                disabled
                name="configState"
                form={form}
                label={t('decoderLabelConfigState')}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  width: { xs: '377px' },
                  marginTop: '8px',
                }}
                padding="8px 16px"
              />
              <InputLabelField
                disabled
                name="attention"
                form={form}
                label={t('decoderLabelsAttention')}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  width: { xs: '377px' },
                  marginTop: '8px',
                }}
                padding="8px 16px"
              />
              <InputLabelField
                disabled
                name="value"
                form={form}
                label={t('formsLabelValue')}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  width: { xs: '377px' },
                  marginTop: '8px',
                }}
                padding="8px 16px"
              />

              <Divider sx={{ color: '#EDEDED', m: '16px 0px' }} />

              {/* Line Unit Readings */}
              <Box sx={{ width: '377px' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: '12px' }}
                >
                  <Stack direction="column">
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '12px',
                        lineHeight: '14px',
                        textTransform: 'uppercase',
                        color: 'text.main',
                      }}
                    >
                      {t('lineUnitReadings')}
                    </Typography>
                    {dateUpdateControl && !loadingControl && (
                      <Typography
                        sx={{ fontSize: '11px', mt: '4px', color: 'grey.600' }}
                      >
                        {t('lastUpdate')}{' '}
                        {formatDateByTimezone(
                          dateUpdateControl.toISOString(),
                        )}
                      </Typography>
                    )}
                  </Stack>
                  <LoadingButton
                    variant="contained"
                    type="button"
                    size="small"
                    loading={loadingControl}
                    onClick={() => {
                      fetchControl()
                      setDateControl(new Date())
                    }}
                    sx={{ minWidth: '36px', height: '28px' }}
                    color="secondary"
                  >
                    <Refresh />
                  </LoadingButton>
                </Stack>
                {loadingControl ? (
                  <Stack
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 80,
                    }}
                  >
                    <CircularProgress size={20} />
                  </Stack>
                ) : (
                  <>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: '8px' }}
                    >
                      <Typography
                        sx={{ fontSize: '12px', color: 'grey.600' }}
                      >
                        {t('lineResistance')}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.main' }}>
                        {dataLineUnitControl?.resistance != null
                          ? `${dataLineUnitControl.resistance} ohm`
                          : '-'}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: '8px' }}
                    >
                      <Typography
                        sx={{ fontSize: '12px', color: 'grey.600' }}
                      >
                        {t('operationsTemperature')}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.main' }}>
                        {dataLineUnitControl?.temperature != null
                          ? `${convertFromMetric(
                              dataLineUnitControl.temperature,
                              'C',
                            )} ${convertLabelUnit('C')}`
                          : '-'}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: '8px' }}
                    >
                      <Typography
                        sx={{ fontSize: '12px', color: 'grey.600' }}
                      >
                        Voltage
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.main' }}>
                        {dataLineUnitControl?.voltage != null
                          ? `${dataLineUnitControl.voltage} mV`
                          : '-'}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: '8px' }}
                    >
                      <Typography
                        sx={{ fontSize: '12px', color: 'grey.600' }}
                      >
                        Leak
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.main' }}>
                        {dataLineUnitControl?.leak != null
                          ? `${dataLineUnitControl.leak} mA`
                          : '-'}
                      </Typography>
                    </Stack>
                  </>
                )}
              </Box>

              <Divider sx={{ color: '#EDEDED', m: '16px 0px' }} />

              {/* Decoder Readings */}
              <Box sx={{ width: '377px' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: '12px' }}
                >
                  <Stack direction="column">
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '12px',
                        lineHeight: '14px',
                        textTransform: 'uppercase',
                        color: 'text.main',
                      }}
                    >
                      {t('decoderReadings')}
                    </Typography>
                    {dateUpdateDecode && !isLoadingReadings && (
                      <Typography
                        sx={{ fontSize: '11px', mt: '4px', color: 'grey.600' }}
                      >
                        {t('lastUpdate')}{' '}
                        {formatDateByTimezone(
                          dateUpdateDecode.toISOString(),
                        )}
                      </Typography>
                    )}
                  </Stack>
                  <LoadingButton
                    variant="contained"
                    type="button"
                    size="small"
                    loading={isLoadingReadings}
                    onClick={() => {
                      if (isInputDecoder) {
                        fetchInputDecoder()
                      } else {
                        fetchDecoder()
                      }
                      setDateDecoder(new Date())
                    }}
                    sx={{ minWidth: '36px', height: '28px' }}
                    color="secondary"
                  >
                    <Refresh />
                  </LoadingButton>
                </Stack>
                {isLoadingReadings ? (
                  <Stack
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 80,
                    }}
                  >
                    <CircularProgress size={20} />
                  </Stack>
                ) : isInputDecoder ? (
                  <>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: '8px' }}
                    >
                      <Typography
                        sx={{ fontSize: '12px', color: 'grey.600' }}
                      >
                        Value (raw)
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.main' }}>
                        {dataInputDecoder?.io?.rawValue != null
                          ? dataInputDecoder.io.rawValue
                          : '-'}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: '8px' }}
                    >
                      <Typography
                        sx={{ fontSize: '12px', color: 'grey.600' }}
                      >
                        Value (expr 1)
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.main' }}>
                        {dataInputDecoder?.io?.value != null
                          ? dataInputDecoder.io.value
                          : '-'}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: '8px' }}
                    >
                      <Typography
                        sx={{ fontSize: '12px', color: 'grey.600' }}
                      >
                        Value (expr 2)
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.main' }}>
                        {dataInputDecoder?.io?.expr2Value != null
                          ? dataInputDecoder.io.expr2Value
                          : '-'}
                      </Typography>
                    </Stack>
                  </>
                ) : (
                  <>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: '8px' }}
                    >
                      <Typography
                        sx={{ fontSize: '12px', color: 'grey.600' }}
                      >
                        Value
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.main' }}>
                        {formatDecoderValue(dataLineUnitDecoder?.rawValue, t)}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: '8px' }}
                    >
                      <Typography
                        sx={{ fontSize: '12px', color: 'grey.600' }}
                      >
                        Switch Parameters
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.main' }}>
                        {dataLineUnitDecoder?.switchParameters
                          ? `${dataLineUnitDecoder.switchParameters.inrush} V, ${dataLineUnitDecoder.switchParameters.inrushTime} ms, ${dataLineUnitDecoder.switchParameters.hold} V`
                          : '-'}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: '8px' }}
                    >
                      <Typography
                        sx={{ fontSize: '12px', color: 'grey.600' }}
                      >
                        {t('commonUnitsResistance')}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.main' }}>
                        {dataLineUnitDecoder?.resistance != null
                          ? `${dataLineUnitDecoder.resistance} ohm`
                          : '-'}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: '8px' }}
                    >
                      <Typography
                        sx={{ fontSize: '12px', color: 'grey.600' }}
                      >
                        {t('commonUnitsInductance')}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: 'text.main' }}>
                        {dataLineUnitDecoder?.inductance != null
                          ? `${dataLineUnitDecoder.inductance} mH`
                          : '-'}
                      </Typography>
                    </Stack>
                  </>
                )}
              </Box>

              {/* Jon 21st Nov 2025: disable this option, to be removed or design a new feature combined with ping/on/off
              <Divider sx={{ color: '#EDEDED', m: '10px 0px' }} />
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '14px',
                  letterSpacing: '0.02em',
                  textAlign: 'left',
                  mb: '10px',
                  color: 'text.main',
                }}
              >
                {t('decoderCONTROL')}
              </Typography>
              <Stack
                direction={'row'}
                alignItems={'center'}
                gap={1}
                mb={'10px'}
              >
                <AntSwitch
                  form={form}
                  name="overrideOptions"
                  width={36}
                  height={19.2}
                  widthThumb={14.4}
                  heightThumb={14.4}
                  borderThumb={14.4}
                  borderTrack={19 / 2}
                  transform={'16'}
                />

                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 700,
                    lineHeight: '16px',
                    letterSpacing: '0em',
                    textAlign: 'left',
                  }}
                >
                  {t('overrideOption')}
                </Typography>
              </Stack>
              {switchOption && (
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  gap={'10px'}
                  mb={'10px'}
                >
                  <Stack
                    sx={{
                      width: 146,
                      height: 55,
                      padding: '8px 16px 8px 16px',
                      borderRadius: '8px',
                      gap: '4px',
                      backgroundColor: 'background.overrideDefault',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 700,
                        lineHeight: '16px',
                        letterSpacing: '0em',
                        textAlign: 'left',
                      }}
                    >
                      {t('decoderVerify')}
                    </Typography>
                    <AntSwitch
                      form={form}
                      name="verify"
                      width={36}
                      height={19.2}
                      widthThumb={14.4}
                      heightThumb={14.4}
                      borderThumb={14.4}
                      borderTrack={19 / 2}
                      transform={'16'}
                    />
                  </Stack>
                  <Stack
                    sx={{
                      width: 146,
                      height: 55,
                      padding: '8px 16px 8px 16px',
                      borderRadius: '8px',
                      gap: '4px',
                      backgroundColor: 'background.overrideDefault',
                    }}
                  >
                    <Typography
                      sx={{
                        //styleName: Text/Text XS Bold;
                        fontSize: '12px',
                        fontWeight: 700,
                        lineHeight: '16px',
                        letterSpacing: '0em',
                        textAlign: 'left',
                      }}
                    >
                      {t('commonUnitsForce')}{' '}
                    </Typography>
                    <AntSwitch
                      form={form}
                      name="force"
                      width={36}
                      height={19.2}
                      widthThumb={14.4}
                      heightThumb={14.4}
                      borderThumb={14.4}
                      borderTrack={19 / 2}
                      transform={'16'}
                    />
                  </Stack>
                </Stack>
              )}
              <Stack
                direction={'row'}
                alignItems={'center'}
                gap={1}
                m={'10px 0px'}
              >
                <AntSwitch
                  form={form}
                  name="overrideSwitchParams"
                  width={36}
                  height={19.2}
                  widthThumb={14.4}
                  heightThumb={14.4}
                  borderThumb={14.4}
                  borderTrack={19 / 2}
                  transform={'16'}
                />
                <Typography
                  sx={{
                    //styleName: Text/Text XS Bold;
                    fontSize: '12px',
                    fontWeight: 700,
                    lineHeight: '16px',
                    letterSpacing: '0em',
                    textAlign: 'left',
                  }}
                >
                  {t('overrideSwitchParameters')}
                </Typography>
              </Stack>
              {switchParameters && (
                <>
                  <InputLabelField
                    name="inrushVoltage"
                    form={form}
                    label={t('decoderInrushVoltage')}
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '377px' },
                      marginTop: '8px',
                    }}
                    padding="8px 16px"
                  />
                  <InputLabelField
                    name="inrushTime"
                    placeholder={'99'}
                    form={form}
                    label={`${t('decoderInrushTime')} (ms)`}
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '377px' },
                      marginTop: '8px',
                    }}
                    padding="8px 16px"
                  />
                  <InputLabelField
                    name="holdingVoltage"
                    form={form}
                    label={t('decoderHoldingVoltage')}
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '377px' },
                      marginTop: '8px',
                    }}
                    padding="8px 16px"
                  />
                </>
              )}
                 */}
            </Stack>
          </form>
        </Fade>
      )}
    </Container>
  )
}

export default ControllersView
